import { drawTimeAxis } from "./axes/TimeAxis";
import { drawPriceAxis } from "./axes/priceAxis";
import { axisFont } from "./text/axisFont";
import { drawLastPriceBadge, drawLastPriceLine, lastPriceBadgeRect } from "./overlays/lastPrice";
import { drawLastPointDot } from "./overlays/lastPointDot";
import { resolveCountdown, drawCountdown } from "./overlays/countdown";
import { drawHighLowLabels } from "./overlays/highLow";
import { preparePositionsText } from "./overlays/positions/positionsText";
import { drawIndicatorLabels } from "./indicators/drawIndicatorLabels";

/**
 * Draws the text layer of a frame: signal labels of indicators (under the
 * rest), axes (with the labels of oscillator
 * panes), the Line dot, the last price badge (with the real last price,
 * pinned to the edge with ▲ / ▼ when the price is out of view),
 * the bar timer while it is on and the last bar is visible, the high / low
 * labels of the visible bars, the pills, cards and price badges of open
 * positions and the handles of drawings on top (prices of drawings go
 * under the last price badge, like those of positions). Uses the layout of the frame and the scales as they
 * were drawn, so it can be redrawn alone (the timer ticks every second).
 *
 * @param {object} engine - ChartEngine: animator, data, series, indicators, scales, textLayer, theme, toggles, positions, size
 * @param {object} frame - from drawFrame
 * @param {{ width: number, height: number }} frame.pane - the price pane
 * @param {number} frame.plotHeight - all panes above the time axis
 * @param {object[]} frame.indicatorPanes - oscillator panes with their price labels
 * @param {{ first: number, last: number }} frame.range
 * @param {object[]} frame.priceLabels
 * @param {number} frame.priceAxisWidth
 * @param {object[]} frame.timeTicks
 * @param {object|null} frame.lastPrice - from resolveLastPrice
 * @returns {boolean} true when the bar timer is drawn
 */
export const drawTextLayer = (engine, frame) => {
  const { data, series, timeScale, priceScale, textLayer, theme, toggles } = engine;
  const { pane, plotHeight, indicatorPanes, range, priceLabels, priceAxisWidth, timeTicks, lastPrice } = frame;
  const { store, priceFormat } = data;
  const { colors } = theme;
  const { lastValue } = series;
  const font = axisFont(theme.fontFamily);
  const positionsText = preparePositionsText(engine, frame);

  const lastPriceRect = lastPrice ? lastPriceBadgeRect(lastPrice, pane, priceAxisWidth) : null;

  textLayer.beginFrame();
  drawIndicatorLabels(textLayer, engine.indicators.priceIndicators, {
    timeScale,
    priceScale,
    pane,
    range,
    fontFamily: theme.fontFamily,
    textColor: colors.indicatorLabelText.css,
  });
  engine.drawings.drawLevelLabels(textLayer, { pane, decimals: priceFormat.decimals });
  drawPriceAxis(textLayer, priceLabels, pane, theme);
  // Only the part of an oscillator pane that is not scrolled away gets labels
  indicatorPanes.forEach(({ priceLabels: paneLabels, width, visibleTop, visibleHeight }) =>
    drawPriceAxis(textLayer, paneLabels, { width, top: visibleTop, height: visibleHeight }, theme),
  );
  drawTimeAxis(textLayer, timeTicks, plotHeight, theme, !engine.animator.isFollowingView);
  if (lastPrice) drawLastPriceLine(textLayer, lastPrice, pane);

  positionsText.drawBadges();
  engine.drawings.drawPrices(textLayer, { pane, decimals: priceFormat.decimals });

  if (series.isLine && lastValue) {
    drawLastPointDot(textLayer, { lastValue, timeScale, priceScale, pane });
  }

  if (lastPrice) {
    drawLastPriceBadge(textLayer, { ...lastPrice, price: store.bars[store.lastIndex].close }, {
      rect: lastPriceRect,
      decimals: priceFormat.decimals,
      theme,
    });
  }

  const isCountdownShown = toggles.timerEnabled && Boolean(lastValue) && timeScale.isLastBarVisible();
  let countdown = null;

  if (isCountdownShown) {
    const countdownStyles = [
      { font: axisFont(theme.fontFamily, true), color: colors.countdownResolutionText.css },
      { font, color: colors.countdownText.css },
    ];

    countdown = resolveCountdown({
      lastValue,
      lastBarTime: store.bars[store.lastIndex].time,
      barDurationMs: data.barDurationMs,
      resolution: data.resolution,
      timeScale,
      priceScale,
      pane,
      measure: textLayer.measure,
      fonts: countdownStyles.map((style) => style.font),
      now: Date.now(),
    });
    drawCountdown(textLayer, countdown, countdownStyles);
  }

  const extremes = series.extremes(range.first, range.last);

  if (extremes) {
    drawHighLowLabels(textLayer, extremes, {
      timeScale,
      priceScale,
      pane,
      decimals: priceFormat.decimals,
      timerRect: countdown?.rect || null,
      font,
      style: { background: colors.highLowLabel.css, color: colors.highLowLabelText.css },
    });
  }

  positionsText.drawLabels();
  engine.drawings.drawHandles(textLayer, pane);

  return Boolean(countdown);
};
